import { getAuthProvider } from '@/shared/lib/auth/auth-config'
import { ApiError } from '@/shared/lib/api/api-client'
import { ingestionUrl } from '@/shared/lib/api/api-base'
import { acceptLanguageHeader } from '@/shared/lib/api/accept-language'

// Dedicated client for the TH.Ingestion (parse-templates) API exposed via
// th_api_gateway. Same JWT Bearer auth contract as the core/reconciliation
// clients; the destination is discriminated purely by the `/api/ingestion`
// path prefix that `ingestionUrl` prepends (the gateway routes that prefix to
// th_ingestion). The SPA NEVER sends X-Tenant-Uuid / X-User-Id — those are
// gateway-injected from JWT claims. Every request carries `Accept-Language` with the
// current UI language so the API can localise user-facing text (upload errors, chat).
//
// Multipart contract: `requestFormData` does NOT set `Content-Type` manually —
// the browser writes `multipart/form-data; boundary=...` from the FormData
// payload. Setting it explicitly destroys the boundary and the backend rejects
// the request as malformed.
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestInit {
  method: HttpMethod
  body?: unknown
  signal?: AbortSignal
}

// th_ingestion's ValidationFilter returns `errors` as an array of objects
// ({ field, errorCode, message }) — NOT ASP.NET's Record<string, string[]>.
// Some endpoints still use a plain string[]. We also keep the legacy
// Record<string, string[]> branch as a fallback. Each failure becomes one
// line so the editor (which reads `error.errors`) can show them individually.
function parseErrorsBody(status: number, raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((x) => {
      if (typeof x === 'string') return [x]
      if (x && typeof x === 'object' && typeof (x as { message?: unknown }).message === 'string') {
        return [(x as { message: string }).message]
      }
      return []
    })
  }
  if (status === 400 && raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>).flatMap(([field, msgs]) => {
      if (!Array.isArray(msgs)) return []
      return msgs
        .filter((m): m is string => typeof m === 'string')
        .map((m) => `${field}: ${m}`)
    })
  }
  return []
}

// th_ingestion reports business errors in one of two shapes: ValidationFilter returns an
// `errors` array, while AppException returns an ErrorResponse whose human message lives in
// `title`. Prefer the array; fall back to `title` so callers surface the real reason instead
// of a generic "Request failed: <status>".
export function extractErrorMessages(status: number, body: unknown): string[] {
  const fromErrorsArray = parseErrorsBody(status, (body as { errors?: unknown } | null)?.errors)
  if (fromErrorsArray.length > 0) return fromErrorsArray

  const title = (body as { title?: unknown } | null)?.title
  return typeof title === 'string' && title.trim() !== '' ? [title] : []
}

async function parseErrorBody(response: Response): Promise<{
  errors: string[]
  details: Record<string, unknown> | undefined
}> {
  let errors: string[] = []
  let details: Record<string, unknown> | undefined
  try {
    const json = (await response.json()) as Record<string, unknown>
    if (json && typeof json === 'object') {
      details = json
    }
    errors = extractErrorMessages(response.status, json)
  } catch {
    // ignore parse error
  }
  return { errors, details }
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const { method, body, signal } = init

  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...acceptLanguageHeader(),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(ingestionUrl(path), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }

  if (response.status === 403) {
    throw new ApiError(403, ['Forbidden'], 'Insufficient permissions')
  }

  if (!response.ok) {
    const { errors, details } = await parseErrorBody(response)
    throw new ApiError(
      response.status,
      errors,
      errors[0] ?? `Request failed: ${response.status}`,
      details,
    )
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  return response.json() as Promise<T>
}

async function requestFormData<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal,
): Promise<T> {
  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  // IMPORTANT: do NOT set `Content-Type` here — the browser writes the
  // multipart boundary from the FormData body. Setting it manually breaks it.
  const headers: Record<string, string> = { ...acceptLanguageHeader() }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(ingestionUrl(path), {
    method: 'POST',
    headers,
    body: formData,
    signal,
  })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }

  if (response.status === 403) {
    throw new ApiError(403, ['Forbidden'], 'Insufficient permissions')
  }

  if (!response.ok) {
    const { errors, details } = await parseErrorBody(response)
    throw new ApiError(
      response.status,
      errors,
      errors[0] ?? `Request failed: ${response.status}`,
      details,
    )
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  return response.json() as Promise<T>
}

async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const authProvider = getAuthProvider()
  const token = await authProvider.getToken()

  const headers: Record<string, string> = { ...acceptLanguageHeader() }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(ingestionUrl(path), { method: 'GET', headers, signal })

  if (response.status === 401) {
    await authProvider.logout()
    throw new ApiError(401, ['Unauthorized'], 'Session expired')
  }

  if (!response.ok) {
    const { errors, details } = await parseErrorBody(response)
    throw new ApiError(
      response.status,
      errors,
      errors[0] ?? `Request failed: ${response.status}`,
      details,
    )
  }

  return response.blob()
}

export const ingestionClient = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', signal }),

  getBlob: (path: string, signal?: AbortSignal) => requestBlob(path, signal),

  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),

  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body, signal }),

  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body, signal }),

  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'DELETE', signal }),

  postForm: <T>(path: string, formData: FormData, signal?: AbortSignal) =>
    requestFormData<T>(path, formData, signal),
}
